import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, ptBR } from '@mui/x-data-grid';
import { apiKeyService } from '../../services/apiKeyService';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CustomPagination from '../../components/CustomPagination';

interface ApiKey {
  id: number;
  name: string;
  api_key_suffix: string;
  created_date: string;
  last_used?: string;
}

const ApiKeys = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalRows, setTotalRows] = useState(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deletingName, setDeletingName] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const columns: GridColDef[] = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 90 
    },
    { 
      field: 'name', 
      headerName: 'Nome', 
      flex: 1,
      minWidth: 200,
    },
    { 
      field: 'api_key_suffix',
      headerName: 'Sufixo da API Key',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Chip
          label={`${params.value}`}
          size="small"
          sx={{ 
            fontFamily: 'monospace',
          }}
        />
      ),
    },
    { 
      field: 'created_date', 
      headerName: 'Data de Criação', 
      flex: 1,
      minWidth: 180,
      valueFormatter: (params) => {
        return format(new Date(params.value), 'dd/MM/yyyy HH:mm');
      },
    },
    { 
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Editar API Key">
            <IconButton
              onClick={() => handleEditClick(params.row.id, params.row.name)}
              size="small"
              color="primary"
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Deletar API Key">
            <IconButton
              onClick={() => handleDeleteClick(params.row.id, params.row.name)}
              size="small"
              color="error"
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const fetchApiKeys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiKeyService.listApiKeys(
        paginationModel.page + 1,
        paginationModel.pageSize
      );
      
      setApiKeys(response.content);
      setTotalRows(response.total);
    } catch (err) {
      setError('Erro ao carregar API Keys. Tente novamente mais tarde.');
      console.error('Error fetching API keys:', err);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  const handleDeleteClick = (id: number, name: string) => {
    setDeletingId(id);
    setDeletingName(name);
  };

  const handleDeleteConfirm = async () => {
    if (deletingId === null) return;

    try {
      setIsDeleting(true);
      await apiKeyService.deleteApiKey(deletingId);
      fetchApiKeys(); // Recarrega a lista
    } catch (err) {
      setError('Erro ao deletar API Key. Tente novamente mais tarde.');
      console.error('Error deleting API key:', err);
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
      setDeletingName('');
    }
  };

  const handleDeleteCancel = () => {
    setDeletingId(null);
    setDeletingName('');
  };

  const handleEditClick = (id: number, name: string) => {
    setEditingId(id);
    setEditingName(name);
  };

  const handleEditConfirm = async () => {
    if (editingId === null) return;

    try {
      setIsEditing(true);
      await apiKeyService.updateApiKey(editingId, { name: editingName });
      fetchApiKeys(); // Recarrega a lista
    } catch (err) {
      setError('Erro ao atualizar API Key. Tente novamente mais tarde.');
      console.error('Error updating API key:', err);
    } finally {
      setIsEditing(false);
      setEditingId(null);
      setEditingName('');
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3
      }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          API Keys
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/api-keys/create')}
        >
          Criar API Key
        </Button>
      </Box>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={apiKeys}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            rowCount={totalRows}
            loading={loading}
            paginationMode="server"
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            disableRowSelectionOnClick
            slots={{
              pagination: CustomPagination,
            }}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
            }}
          />
        </Box>
      </Paper>

      <Dialog
        open={deletingId !== null}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          Tem certeza que deseja excluir a API Key "{deletingName}"?
          Esta ação não pode ser desfeita.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteCancel}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deletando...' : 'Deletar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editingId !== null}
        onClose={handleEditCancel}
      >
        <DialogTitle>Editar API Key</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome da API Key"
            fullWidth
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            required
            disabled={isEditing}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEditCancel}
            disabled={isEditing}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditConfirm}
            variant="contained"
            disabled={isEditing || !editingName.trim()}
            startIcon={isEditing ? <CircularProgress size={20} /> : null}
          >
            {isEditing ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApiKeys; 